import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  MapPin, Phone, Globe, CheckCircle, Lock, ArrowRight,
  Shield, Building2, ExternalLink, ChevronRight, Star
} from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { INDUSTRY_TYPES, getIndustryType } from "@/constants/industries";
import { getAffiliates } from "@/constants/affiliates";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Convert URL slug to DB value: "tax-pro" → "tax_pro"
const slugToValue = (slug) => slug.replace(/-/g, "_");
// Convert DB value to URL slug: "tax_pro" → "tax-pro"
export const valueToSlug = (value) => value.replace(/_/g, "-");

function AffiliateCard({ affiliate }) {
  return (
    <a
      href={affiliate.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`affiliate-card-${affiliate.name.replace(/\s+/g, "-").toLowerCase()}`}
      className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:border-[#003366]/40 hover:shadow-sm transition-all group"
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-black flex-shrink-0"
        style={{ backgroundColor: affiliate.color }}
      >
        {affiliate.badge}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-bold text-[#0F172A] text-sm">{affiliate.name}</span>
          <ExternalLink size={12} className="text-slate-400 group-hover:text-[#003366] transition-colors" />
        </div>
        <p className="text-xs text-[#64748B] mt-0.5 leading-relaxed">{affiliate.tagline}</p>
      </div>
      <ChevronRight size={16} className="text-slate-300 group-hover:text-[#003366] flex-shrink-0 mt-0.5 transition-colors" />
    </a>
  );
}

function NearbyZipRow({ zip, city, state, available }) {
  return (
    <Link
      to={`/find-provider?zip=${zip}`}
      className={`flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm transition-all ${
        available
          ? "bg-[#F0FDF4] border-[#2ECC71]/30 hover:border-[#2ECC71] hover:shadow-sm"
          : "bg-[#F8FAFC] border-slate-200 opacity-70"
      }`}
      data-testid={`nearby-zip-${zip}`}
    >
      <span>
        <strong className="text-[#0F172A]">{zip}</strong>
        {city && <span className="text-[#64748B] ml-2 text-xs">{city}, {state}</span>}
      </span>
      {available ? (
        <span className="text-xs font-bold text-[#27AE60] bg-[#2ECC71]/10 px-2 py-0.5 rounded-full flex items-center gap-1">
          <CheckCircle size={10} /> Available
        </span>
      ) : (
        <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full flex items-center gap-1">
          <Lock size={10} /> Claimed
        </span>
      )}
    </Link>
  );
}

export default function ZipLandingPage() {
  const { industrySlug, zip } = useParams();
  const navigate = useNavigate();
  const industryValue = slugToValue(industrySlug || "tax-pro");
  const industry = getIndustryType(industryValue);
  const affiliates = getAffiliates(industryValue);

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [leadForm, setLeadForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [leadSent, setLeadSent] = useState(false);
  const [leadSending, setLeadSending] = useState(false);

  useEffect(() => {
    if (!zip || zip.length !== 5 || !/^\d{5}$/.test(zip)) {
      setError("Invalid ZIP code");
      setLoading(false);
      return;
    }
    fetchPageData();
  }, [zip, industryValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove the old document.title useEffect — SEOHead handles it


  const fetchPageData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/seo/local-page?industry=${industryValue}&zip=${zip}`);
      setPageData(res.data);
    } catch (e) {
      setError("Unable to load page data. Please try again.");
    }
    setLoading(false);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadSending(true);
    try {
      await axios.post(`${API}/leads`, {
        zip_code: zip,
        taxpayer_name: leadForm.name,
        taxpayer_phone: leadForm.phone,
        taxpayer_email: leadForm.email,
        message: leadForm.message,
      });
      setLeadSent(true);
    } catch {
      // silent
    }
    setLeadSending(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-[#003366] border-t-transparent rounded-full animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="section-container py-20 text-center">
          <h1 className="text-2xl font-bold text-[#0F172A] mb-4">Page Not Found</h1>
          <p className="text-[#64748B] mb-6">{error || "This ZIP code or industry is not recognized."}</p>
          <Link to="/find-provider" className="btn-action">Find a Provider</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { zip_info, is_claimed, provider, nearby_zips, industry_label } = pageData;
  const city = zip_info?.city || "";
  const state = zip_info?.state || "";
  const location = city ? `${city}, ${state}` : zip;
  const IconComp = industry?.icon;

  const metaTitle = `${industry_label} in ${location} (ZIP ${zip})`;
  const metaDesc = is_claimed
    ? `Find a verified ${industry_label} in ${location} (ZIP ${zip}). Exclusive local directory listing. Contact them today on NextDayRefund.com.`
    : `Looking for a ${industry_label} in ${location} (ZIP ${zip})? Browse verified providers and top-rated national options serving your area. Claim this territory for $150.`;

  // Rich JSON-LD schemas
  const localBusinessSchema = is_claimed && provider?.business_name ? {
    "@type": "LocalBusiness",
    "@id": `https://nextdayrefund.com/find/${industrySlug}/${zip}#business`,
    "name": provider.business_name,
    "description": provider.description || `Verified ${industry_label} serving ${location}`,
    "address": {
      "@type": "PostalAddress",
      "postalCode": zip,
      "addressLocality": city,
      "addressRegion": state,
      "addressCountry": "US",
      ...(provider.address ? { "streetAddress": provider.address } : {}),
    },
    "areaServed": { "@type": "PostalAddress", "postalCode": zip, "addressLocality": city, "addressRegion": state },
    ...(provider.phone ? { "telephone": provider.phone } : {}),
    ...(provider.website ? { "url": provider.website } : {}),
    "priceRange": "$",
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "24", "bestRating": "5" },
  } : null;

  const faqSchema = {
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `Is there a ${industry_label} in ${location} (ZIP ${zip})?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": is_claimed && provider?.business_name
            ? `Yes — ${provider.business_name} is the verified ${industry_label} for ZIP ${zip} in ${location}.`
            : `No local ${industry_label} has claimed ZIP ${zip} in ${location} yet. Check back or browse national partners above.`,
        },
      },
      {
        "@type": "Question",
        "name": `How do I contact a ${industry_label} near ${location}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Use the contact form on this page to reach the verified ${industry_label} serving ZIP ${zip} in ${location}, or call them directly using the phone number listed.`,
        },
      },
      {
        "@type": "Question",
        "name": `How do I get listed as a ${industry_label} in ${location}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Claim ZIP code ${zip} on NextDayRefund.com for $150/year to become the exclusive verified ${industry_label} for ${location}. Once claimed, you receive all leads from this territory.`,
        },
      },
    ],
  };

  const webPageSchema = {
    "@type": "WebPage",
    "@id": `https://nextdayrefund.com/find/${industrySlug}/${zip}`,
    "name": metaTitle,
    "description": metaDesc,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://nextdayrefund.com/" },
        { "@type": "ListItem", "position": 2, "name": industry_label, "item": `https://nextdayrefund.com/find/${industrySlug}` },
        { "@type": "ListItem", "position": 3, "name": `${location} (${zip})`, "item": `https://nextdayrefund.com/find/${industrySlug}/${zip}` },
      ],
    },
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SEOHead
        title={metaTitle}
        description={metaDesc}
        canonical={`/find/${industrySlug}/${zip}`}
        jsonLd={[webPageSchema, faqSchema, ...(localBusinessSchema ? [localBusinessSchema] : [])]}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: industry_label, url: `/find/${industrySlug}` },
          { name: `${location} — ZIP ${zip}`, url: `/find/${industrySlug}/${zip}` },
        ]}
      />

      <Navbar />

      {/* Hero Header */}
      <section className="bg-[#003366] py-10">
        <div className="section-container">
          <div className="flex items-center gap-3 mb-3">
            {IconComp && (
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: industry?.color + "22" }}>
                <IconComp size={20} style={{ color: industry?.color || "#2ECC71" }} />
              </div>
            )}
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-widest font-semibold">{location} · ZIP {zip}</p>
              <h1 className="text-2xl sm:text-3xl font-black text-white leading-tight">
                {industry_label} in {location}
              </h1>
            </div>
          </div>
          <p className="text-slate-300 text-sm max-w-lg">
            {is_claimed
              ? `A verified ${industry_label} holds the exclusive listing for ${location}, ZIP ${zip}. Contact them directly below.`
              : `No local ${industry_label} has claimed ZIP ${zip} in ${location} yet. Browse our trusted national partners below, or claim this territory for $150.`}
          </p>
        </div>
      </section>

      <div className="section-container py-8 max-w-3xl">

        {/* CLAIMED — Show Provider */}
        {is_claimed && provider && (
          <div className="mb-6">
            <div className="bg-white border border-[#2ECC71]/30 rounded-xl overflow-hidden">
              <div className="bg-[#F0FDF4] border-b border-[#2ECC71]/20 px-6 py-4 flex items-center gap-3">
                <CheckCircle size={20} className="text-[#27AE60]" />
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[#27AE60]">Verified Exclusive Provider</div>
                  <div className="text-[#0F172A] font-bold text-lg leading-tight">{provider.business_name}</div>
                </div>
                <div className="ml-auto hidden sm:flex items-center gap-1 text-[#27AE60]">
                  {[1,2,3,4,5].map(s => <Star key={s} size={13} fill="currentColor" />)}
                </div>
              </div>

              <div className="px-6 py-5 space-y-3">
                {provider.description && (
                  <p className="text-[#334155] text-sm leading-relaxed">{provider.description}</p>
                )}
                <div className="flex flex-wrap gap-4 text-sm">
                  {provider.phone && (
                    <a href={`tel:${provider.phone}`} className="flex items-center gap-2 text-[#003366] font-semibold hover:underline" data-testid="provider-phone">
                      <Phone size={14} /> {provider.phone}
                    </a>
                  )}
                  {provider.website && (
                    <a href={provider.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#003366] font-semibold hover:underline" data-testid="provider-website">
                      <Globe size={14} /> Visit Website
                    </a>
                  )}
                  {city && (
                    <span className="flex items-center gap-2 text-[#64748B]">
                      <MapPin size={14} /> {location}
                    </span>
                  )}
                </div>
              </div>

              {/* Contact Form */}
              <div className="border-t border-slate-100 px-6 py-5">
                <h3 className="font-bold text-[#0F172A] mb-4 text-sm">Send a Message to This Provider</h3>
                {leadSent ? (
                  <div className="flex items-center gap-2 text-[#27AE60] font-semibold text-sm py-3" data-testid="lead-sent-confirmation">
                    <CheckCircle size={18} /> Your message has been sent! They'll contact you shortly.
                  </div>
                ) : (
                  <form onSubmit={submitLead} className="space-y-3" data-testid="provider-contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input type="text" placeholder="Your Name" value={leadForm.name} onChange={e => setLeadForm(f => ({...f, name: e.target.value}))} className="input-field !py-2.5 !text-sm" data-testid="lead-name" />
                      <input type="tel" placeholder="Your Phone" value={leadForm.phone} onChange={e => setLeadForm(f => ({...f, phone: e.target.value}))} className="input-field !py-2.5 !text-sm" data-testid="lead-phone" />
                    </div>
                    <input type="email" placeholder="Your Email" value={leadForm.email} onChange={e => setLeadForm(f => ({...f, email: e.target.value}))} className="input-field !py-2.5 !text-sm" data-testid="lead-email" />
                    <textarea placeholder="Brief message (optional)" value={leadForm.message} onChange={e => setLeadForm(f => ({...f, message: e.target.value}))} rows={2} className="input-field !py-2.5 !text-sm resize-none" data-testid="lead-message" />
                    <button type="submit" disabled={leadSending} className="btn-action w-full !py-2.5 !text-sm flex items-center justify-center gap-2" data-testid="lead-submit">
                      {leadSending ? "Sending..." : <><ArrowRight size={15} /> Send Message</>}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}

        {/* UNCLAIMED — Show Affiliate Fallback */}
        {!is_claimed && (
          <div className="mb-6">
            {/* Claim CTA Banner */}
            <div className="bg-[#003366] rounded-xl p-5 mb-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <div className="text-[#2ECC71] text-xs font-bold uppercase tracking-widest mb-1">Territory Available</div>
                <h2 className="text-white font-black text-lg">Be the Only {industry_label} in ZIP {zip}</h2>
                <p className="text-slate-300 text-sm mt-1">Claim exclusive rights — every lead from {location} goes directly to you.</p>
              </div>
              <Link
                to={`/claim?zip=${zip}`}
                className="btn-action whitespace-nowrap flex items-center gap-2 !py-2.5 !px-5"
                data-testid="unclaimed-claim-cta"
              >
                Claim This ZIP <ArrowRight size={16} />
              </Link>
            </div>

            {/* Affiliate Cards */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <Building2 size={16} className="text-[#64748B]" />
                <h3 className="font-bold text-[#0F172A] text-sm">Trusted National {industry_label} Partners</h3>
              </div>
              <p className="text-xs text-[#64748B] mb-4">No local provider has claimed this ZIP yet. Here are top-rated national options serving your area:</p>
              <div className="space-y-3" data-testid="affiliate-cards">
                {affiliates.map((aff, i) => (
                  <AffiliateCard key={i} affiliate={aff} />
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 text-center">
                <p className="text-xs text-[#64748B] mb-3">Are you a local {industry_label} in {location}? Claim this ZIP and get listed exclusively.</p>
                <Link to={`/claim?zip=${zip}`} className="text-[#003366] font-semibold text-sm hover:underline flex items-center justify-center gap-1" data-testid="affiliate-section-claim-link">
                  Claim ZIP {zip} for $150 <ChevronRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* SEO Content Block */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <h2 className="text-base font-bold text-[#003366] mb-3">
            {is_claimed
              ? `About ${provider?.business_name || `This ${industry_label}`} in ${location}`
              : `Find a ${industry_label} Near ${location} (ZIP ${zip})`}
          </h2>
          <div className="text-sm text-slate-600 leading-relaxed space-y-2">
            {is_claimed ? (
              <>
                <p>
                  <strong>{provider?.business_name}</strong> is the verified exclusive <strong>{industry_label}</strong> for ZIP code {zip} in <strong>{location}</strong> on NextDayRefund.com. They serve taxpayers and residents in {city}{state ? `, ${state}` : ""} and surrounding areas.
                </p>
                <p>
                  As the exclusive {industry_label} for this territory, {provider?.business_name} receives all incoming inquiries from ZIP {zip} directly — making them your primary local contact for {industry_label} services in {location}.
                </p>
              </>
            ) : (
              <>
                <p>
                  No local <strong>{industry_label}</strong> has yet claimed exclusive advertising rights for ZIP code {zip} in <strong>{location}, {state}</strong>. NextDayRefund.com connects taxpayers with verified local service providers across all 50 US states.
                </p>
                <p>
                  If you are a <strong>{industry_label}</strong> serving {location}, you can claim ZIP {zip} for <strong>$150/year</strong> and become the exclusive advertiser for this territory during the 2027 tax season. Every taxpayer who searches this ZIP will be routed directly to your business.
                </p>
              </>
            )}
            <p>
              NextDayRefund.com serves <strong>12 industries</strong> across every US ZIP code including tax professionals, debt relief, insurance agents, mortgage brokers, financial advisors, real estate agents, and more.
            </p>
          </div>
        </div>

        {/* Browse Other Industries for This ZIP */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
          <h3 className="font-bold text-[#0F172A] text-sm mb-3">Other Industries in ZIP {zip}</h3>
          <div className="flex flex-wrap gap-2">
            {INDUSTRY_TYPES.filter(t => t.value !== "all" && t.value !== industryValue).slice(0, 8).map(ind => {
              const Ic = ind.icon;
              return (
                <Link
                  key={ind.value}
                  to={`/find/${valueToSlug(ind.value)}/${zip}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 text-xs font-semibold text-[#64748B] hover:border-[#003366] hover:text-[#003366] transition-colors"
                  data-testid={`other-industry-${ind.value}`}
                >
                  <Ic size={12} style={{ color: ind.color }} />
                  {ind.label}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Nearby ZIPs */}
        {nearby_zips && nearby_zips.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-bold text-[#0F172A] text-sm mb-3">Nearby ZIP Codes</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {nearby_zips.map(z => (
                <NearbyZipRow key={z.zip} {...z} />
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 text-center">
              <Link to="/find-provider" className="text-xs text-[#003366] font-semibold hover:underline">
                Search All ZIP Codes →
              </Link>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
